import { useQuery, type UseQueryResult } from '@tanstack/react-query'
import { z } from 'zod'

import { proposedGet } from '@/api/proposedEndpoint'
import { brokerKeys } from '@/features/brokers/api/keys'
import { companyMediaUrl } from '@/lib/company-logos'
import type { BrokerPeriod } from '@/schemas/broker'

/**
 * THE switch for Overview's "Stocks traded most" Bought most / Sold most table. mm-be ranks one
 * turnover list (`/brokers/stocks-traded-most/`) and no bought/sold split — market-wide every share
 * bought is a share sold, so the split needs a rule mm-be has not defined yet. So this is `false`:
 * no request is made, and the card draws its real head over "Not available yet". Flip it when the
 * endpoint ships (`mm-brokers-2026-10-28/asks/stocks-traded-most-sides.md`).
 */
export const STOCKS_TRADED_SIDES_AVAILABLE: boolean = false

/** The proposed path. */
const PATH = '/api/v5/brokers/stocks-traded-most/sides/'

/** The mock ranks five a side. */
export const STOCKS_TRADED_SIDES_LIMIT = 5

const tradedSideStockSchema = z.object({
  symbol: z.string().min(1),
  /** Null when mm-be has no company name on file. */
  name: z.string().min(1).nullable(),
  /** Absolute; null when the company has no logo. */
  logoUrl: z.string().min(1).nullable(),
})

const stocksTradedSidesSchema = z.object({
  boughtMost: z.array(tradedSideStockSchema),
  soldMost: z.array(tradedSideStockSchema),
})

export type TradedSideStock = z.infer<typeof tradedSideStockSchema>
export type StocksTradedSides = z.infer<typeof stocksTradedSidesSchema>

interface WireSideStock {
  symbol: string
  name: string | null
  logo_url: string | null
}

interface WireStocksTradedSides {
  bought_most: WireSideStock[]
  sold_most: WireSideStock[]
}

const mapStock = (wire: WireSideStock) => ({
  symbol: wire.symbol,
  name: wire.name === null || wire.name.trim() === '' ? null : wire.name,
  logoUrl: wire.logo_url === null ? null : companyMediaUrl(wire.logo_url),
})

export const stocksTradedSidesService = {
  sides: async (period: BrokerPeriod): Promise<StocksTradedSides> => {
    const wire = await proposedGet<WireStocksTradedSides>(PATH, {
      period,
      limit: STOCKS_TRADED_SIDES_LIMIT,
    })
    return stocksTradedSidesSchema.parse({
      boughtMost: wire.bought_most.map(mapStock),
      soldMost: wire.sold_most.map(mapStock),
    })
  },
}

/** Never runs while `STOCKS_TRADED_SIDES_AVAILABLE` is false. */
export const useStocksTradedSides = (period: BrokerPeriod): UseQueryResult<StocksTradedSides> =>
  useQuery({
    queryKey: [...brokerKeys.all, 'stocks-traded-sides', period] as const,
    queryFn: () => stocksTradedSidesService.sides(period),
    enabled: STOCKS_TRADED_SIDES_AVAILABLE,
  })
